import RequestAbstract from "../abstract";

export default class MarketingCampaigns extends RequestAbstract {
    data;
    constructor(data) {
        super(data);
        this.data = data;
        this.link = "api/marketing-campaigns";
    }

    // Override to use a GET request
    async getAllFiltered(
        filter = [],
        sort = [{ field: 'id', direction: 'DESC' }],
        props = {}
    ) {
        return await this.getFiltered(this.link, filter, sort, {
            page: props.paging?.page || 0,
            pageSize: props.paging?.pageSize || 25
        }, props);
    }

    // Override search to use GET
    async search(limit, filter = [], sort = [], props) {
        return await this.getFiltered(
            this.link,
            filter,
            sort,
            {
                page: 0,
                pageSize: limit || 25
            },
            props
        );
    }

    // Override getFiltered to use a GET request
    async getFiltered(link, filter, sort, paging, props = {}) {
        let url = link;
        
        // Append query params to the URL
        const params = new URLSearchParams();
        
        if (paging) {
            if (paging.page !== undefined) {
                params.append('page', paging.page);
            }
            if (paging.pageSize !== undefined) {
                params.append('pageSize', paging.pageSize);
            }
        }
        
        if (sort && sort.length > 0) {
            params.append('sortField', sort[0].field);
            params.append('sortDirection', sort[0].direction);
        }
        
        if (filter && filter.length > 0) {
            params.append('filter', JSON.stringify(filter));
        }
        
        // Add extra properties if present
        if (props && Object.keys(props).length > 0) {
            Object.entries(props).forEach(([key, value]) => {
                if (key !== 'paging' && value !== undefined) {
                    params.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
                }
            });
        }
        
        if (params.toString()) {
            url += '?' + params.toString();
        }
        
        return await this.get(url);
    }

    // Override add to create new campaigns
    async add(info) {
        console.log('Creating new campaign:', info);
        const response = await this.loadWithAuth(`${this.link}/add`, {
            method: 'POST',
            body: JSON.stringify(info),
            headers: {
                'Content-Type': 'application/json'
            }
        });
        console.log('Create response:', response);
        return response;
    }

    // Override update to use a PUT request
    async update(id, info) {
        console.log(`Updating campaign with ID: ${id}`, info);
        const response = await this.loadWithAuth(`${this.link}/${id}`, {
            method: 'PUT',
            body: JSON.stringify(info),
            headers: {
                'Content-Type': 'application/json'
            }
        });
        console.log('Update response:', response);
        return response;
    }

    // Fetch a single campaign by ID
    async get(url) {
        return await this.loadWithAuth(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });
    }

    // Fetch a campaign by ID (convenience method)
    async getById(id) {
        return await this.get(`${this.link}/${id}`);
    }

    // Upload campaign images
    async uploadImage(formData) {
        const url = `${this.link}/upload-image`;
        return await this.loadWithAuth(url, {
            method: 'POST',
            body: formData
        }, { fileUpload: true });
    }

    // Send a campaign (same as handleSend in the editor)
    async send(id) {
        console.log(`Sending campaign with ID: ${id}`);
        
        // Fetch current campaign data first
        const campaignData = await this.getById(id);
        
        if (campaignData.error !== 'OK') {
            console.error('Failed to get campaign data:', campaignData);
            return campaignData;
        }
        
        console.log('Original campaign data from server:', campaignData.data);
        console.log('All keys in campaign data:', Object.keys(campaignData.data));
        
        // Look up audience-related fields
        const audienceKeys = Object.keys(campaignData.data).filter(key => 
            key.toLowerCase().includes('audience') || 
            key.toLowerCase().includes('list') ||
            key.toLowerCase().includes('user')
        );
        console.log('Audience-related keys:', audienceKeys);
        
        // Check whether audience data is present
        console.log('Audience data check:', {
            audienceListIds: campaignData.data.audienceListIds,
            audienceLists: campaignData.data.audienceLists,
            audienceListUserNumber: campaignData.data.audienceListUserNumber,
            audienceListsNames: campaignData.data.audienceListsNames
        });
        
        // Inspect the audienceLists structure
        console.log('audienceLists type:', typeof campaignData.data.audienceLists);
        console.log('audienceLists value:', campaignData.data.audienceLists);
        if (Array.isArray(campaignData.data.audienceLists)) {
            console.log('audienceLists is array with length:', campaignData.data.audienceLists.length);
            console.log('First element:', campaignData.data.audienceLists[0]);
            console.log('First element type:', typeof campaignData.data.audienceLists[0]);
        }
        
        // Extract audience IDs
        let audienceIds = [];
        if (campaignData.data.audienceLists && Array.isArray(campaignData.data.audienceLists)) {
            audienceIds = campaignData.data.audienceLists.map(audience => {
                // If this is an object with an id field, extract the id
                if (typeof audience === 'object' && audience.id) {
                    return audience.id;
                }
                // If this is already a number, return as is
                if (typeof audience === 'number') {
                    return audience;
                }
                // Try to convert to a number
                return parseInt(audience);
            }).filter(audienceId => !isNaN(audienceId));
        }
        
        console.log('Extracted audience IDs:', audienceIds);
        
        // Send the full payload, same as in the editor
        let preparedData = {
            campaignName: campaignData.data.campaignName,
            imageUrl: campaignData.data.imageUrl,
            audienceListIds: audienceIds, // Use extracted IDs
            audienceListsNames: campaignData.data.audienceListsNames,
            audienceListUserNumber: campaignData.data.audienceListUserNumber,
            deliveryRate: campaignData.data.deliveryRate,
            clickRate: campaignData.data.clickRate,
            languages: campaignData.data.languages,
            title: campaignData.data.title,
            text: campaignData.data.text,
            locationId: campaignData.data.locationId,
            displayEndDate: campaignData.data.displayEndDate,
            scheduledDate: campaignData.data.scheduledDate,
            notificationTiming: campaignData.data.notificationTiming,
            status: 'SENT'
        };
        
        console.log('Full payload prepared:', preparedData);
        
        console.log('Status update with audience data:', preparedData);
        
        console.log('Final data being sent:', preparedData);
        
        const response = await this.loadWithAuth(`${this.link}/${id}`, {
            method: 'PUT',
            body: JSON.stringify(preparedData),
            headers: {
                'Content-Type': 'application/json'
            }
        });
        
        console.log('Send response:', response);
        return response;
    }
}
